/**
 * useYDocStore
 *
 * Zustand store that holds the shared Y.Doc and its top-level Y.Maps.
 * Initialized once when the editor mounts via `initialize()`, cleared on unmount
 * via `cleanup()`.
 *
 * Any component or non-React code that needs Y.Doc access can read from here
 * instead of requiring prop drilling or context threading.
 *
 * @example
 *   // React component
 *   const yReferenceMarks = useYDocStore(state => state.yReferenceMarks);
 *
 *   // Non-React code (plugins, handlers)
 *   const { ydoc, yReferenceMarks } = useYDocStore.getState();
 */

import {create} from 'zustand';

const useYDocStore = create((set) => ({
    /** @type {import('yjs').Doc|null} */
    ydoc: null,

    /** @type {import('@hocuspocus/provider').HocuspocusProvider|import('y-websocket').WebsocketProvider|null} */
    provider: null,

    /** @type {import('yjs').Map|null}  Y.Map("referenceMarks") */
    yReferenceMarks: null,

    /** @type {import('yjs').Map|null}  Y.Map("comments") */
    yComments: null,

    /** @type {import('yjs').Map|null}  Y.Map("documentMeta") */
    yDocMeta: null,

    /** @type {import('yjs').Map|null}  Y.Map("sourcePackage") */
    ySourcePackage: null,

    /** @type {import('yjs').Map|null}  Y.Map("agenticSections") */
    yAgenticSections: null,

    /** @type {import('yjs').Map|null}  Y.Map("annotations") */
    yAnnotations: null,

    /** @type {import('yjs').Map|null}  Y.Map("mapPreviews") */
    yMapPreviews: null,

    /** @type {import('yjs').Map|null}  Y.Map("sitrepGenerations") */
    yMapSitrepGenerations: null,

    /**
     * Initialize the store with the Y.Doc and provider.
     * Called once during editor setup after the Y.Doc is created.
     *
     * @param {import('yjs').Doc}    ydoc
     * @param {object}               provider
     */
    initialize: (ydoc, provider) => {
        set({
            ydoc,
            provider,
            yReferenceMarks: ydoc.getMap('referenceMarks'),
            yComments: ydoc.getMap('comments'),
            yDocMeta: ydoc.getMap('documentMeta'),
            ySourcePackage: ydoc.getMap('sourcePackage'),
            yAgenticSections: ydoc.getMap('agenticSections'),
            yAnnotations: ydoc.getMap('annotations'),
            yMapPreviews: ydoc.getMap('mapPreviews'),
            yMapSitrepGenerations: ydoc.getMap('sitrepGenerations'),
        });
    },

    /**
     * Clear all Y.Doc references.
     * Called when the editor unmounts.
     */
    cleanup: () => {
        set({
            ydoc: null,
            provider: null,
            yReferenceMarks: null,
            yComments: null,
            yDocMeta: null,
            ySourcePackage: null,
            yAgenticSections: null,
            yAnnotations: null,
            yMapPreviews: null,
            yMapSitrepGenerations: null,
        });
    },
}));

export default useYDocStore;

